import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import { LinkedInspection } from "../api/WorkOrders";
import { SelectedEquip } from "../models/equipment/Equipment";
import { find, remove } from "lodash";
import { InspectionData } from "./wo-order";

type EquipmentState = {
  inspections: { [equipId: string]: LinkedInspection[] };
  downloadedEquipments: SelectedEquip[];
  selectedEquip: SelectedEquip[];
  inspectionData: { [equipId: string]: InspectionData[] };
};

type EquipmentAction = {
  setInspectionsForEquipment: (
    equipId: string,
    inspections: LinkedInspection[]
  ) => void;
  getInspectionsForEquipment: (
    equipId: string
  ) => LinkedInspection[] | undefined;
  toggleSelectedEquipment: (equip: SelectedEquip) => void;
  setDownloadedEquip: (items: SelectedEquip[]) => void;
  setEquipInspectionData: (equipId: string, data: InspectionData) => void;
  getInspectionDataForEquip: (
    equipId: string,
    inspectionId: string
  ) => InspectionData | undefined;
  //reset
  resetAllButDownloadedEquips: () => void;
  resetSelectedEquipment: () => void;
  reset: () => void;
};

const defaultState: EquipmentState = {
  inspections: {},
  downloadedEquipments: [],
  selectedEquip: [],
  inspectionData: {},
};

export const useEquipmentStore = create<EquipmentState & EquipmentAction>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        getInspectionsForEquipment(equipId) {
          return get().inspections[equipId];
        },
        setInspectionsForEquipment(equipId, inspections) {
          set((state) => {
            state.inspections = {
              ...state.inspections,
              [equipId]: inspections,
            };
          });
        },
        toggleSelectedEquipment(equip) {
          set((state) => {
            const selectedEquips = [...state.selectedEquip];
            const existingEquip = find(selectedEquips, { id: equip.id });

            if (existingEquip) remove(selectedEquips, { id: equip.id });
            else selectedEquips.push(equip);
            state.selectedEquip = selectedEquips;
          });
        },
        setDownloadedEquip(items) {
          set((state) => {
            state.downloadedEquipments = items;
          });
        },
        resetSelectedEquipment() {
          set((state) => {
            state.selectedEquip = [];
          });
        },
        resetAllButDownloadedEquips() {
          set((state) => {
            const idsToKeep = state.downloadedEquipments.map((d) => d.id);
            const retainKeys = (obj: Record<string, any>) => {
              return Object.fromEntries(
                Object.entries(obj).filter(([key]) => idsToKeep.includes(key))
              );
            };
            state.inspections = retainKeys(state.inspections);
          });
        },
        setEquipInspectionData(equipId, data) {
          set((state) => {
            state.inspectionData = {
              ...state.inspectionData,
              [equipId]: [
                ...((state.inspectionData[equipId] ?? []).filter(
                  (i) => i.inspectionId !== data.inspectionId
                ) ?? []),
                data,
              ],
            };
          });
        },
        getInspectionDataForEquip(equipId, inspectionId) {
          return get().inspectionData[equipId].find(
            (i) => i.inspectionId === inspectionId
          );
        },
        reset() {
          set(() => defaultState);
        },
      })),
      {
        name: "equipment",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          inspections: state.inspections,
          downloadedEquipments: state.downloadedEquipments,
        }),
      }
    ),
    {
      name: "EquipmentStore",
      enabled: true,
    }
  )
);
